import React from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { type MRT_RowData, type MRT_TableInstance } from 'material-react-table'
import { IconActionButton } from 'src/utils/views'

function normalize(text: string) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export type ColumnVisibilityItem = {
    id: string
    title: string
    visible: boolean
    canHide: boolean
}

export type ColumnVisibilityButtonProps = {
    columns: ColumnVisibilityItem[]
    onChange: (ids: string[], visible: boolean) => void
    small?: boolean
}

export function ColumnVisibilityButton({ columns, onChange, small }: ColumnVisibilityButtonProps) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const [search, setSearch] = React.useState('')

    const term = normalize(search.trim())
    const matches = term ? columns.filter(({ title }) => normalize(title).includes(term)) : columns
    const hideable = matches.filter((column) => column.canHide)
    const visibleCount = columns.filter((column) => column.visible).length

    const setAll = (visible: boolean) => {
        onChange(
            hideable.map((column) => column.id),
            visible
        )
    }

    const close = () => {
        setAnchorEl(null)
        setSearch('')
    }

    return (
        <>
            <Tooltip title="Mostrar/ocultar colunas">
                {small ? (
                    <IconActionButton
                        size="small"
                        aria-label="Mostrar/ocultar colunas"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                        <ViewColumnIcon fontSize="small" />
                    </IconActionButton>
                ) : (
                    <IconButton
                        aria-label="Mostrar/ocultar colunas"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                        <ViewColumnIcon />
                    </IconButton>
                )}
            </Tooltip>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 340, maxWidth: '90vw', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 1, pb: 0.5 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Buscar coluna"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => event.stopPropagation()}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                aria-label="Limpar busca"
                                                onClick={() => setSearch('')}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ px: 1, display: 'flex', alignItems: 'center', columnGap: 0.5 }}>
                        <Button size="small" disabled={hideable.length === 0} onClick={() => setAll(true)}>
                            {term ? 'Mostrar encontradas' : 'Mostrar todas'}
                        </Button>
                        <Button size="small" disabled={hideable.length === 0} onClick={() => setAll(false)}>
                            {term ? 'Ocultar encontradas' : 'Ocultar todas'}
                        </Button>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                ml: 'auto',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {visibleCount} de {columns.length}
                        </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 360, overflowY: 'auto', pb: 0.5 }}>
                        {/* MenuList: no MUI 9 o MenuItem exige o contexto de um Menu/MenuList (sem ele a tela fica em branco) */}
                        <MenuList dense disablePadding>
                            {matches.map((column) => (
                                <MenuItem
                                    key={column.id}
                                    dense
                                    disabled={!column.canHide}
                                    onClick={() => onChange([column.id], !column.visible)}
                                    sx={{ py: 0, whiteSpace: 'normal' }}
                                >
                                    <Checkbox
                                        size="small"
                                        edge="start"
                                        disableRipple
                                        checked={column.visible}
                                        tabIndex={-1}
                                    />
                                    {column.title}
                                </MenuItem>
                            ))}
                        </MenuList>
                        {matches.length === 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    px: 2,
                                    py: 1.5
                                }}
                            >
                                Nenhuma coluna encontrada
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Popover>
        </>
    )
}

export function TcmColumnVisibilityButton<TData extends MRT_RowData>({ table }: { table: MRT_TableInstance<TData> }) {
    const columns = table
        .getAllLeafColumns()
        .filter((column) => !column.id.startsWith('mrt-row'))
        .map((column) => ({
            id: column.id,
            title: typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id,
            visible: column.getIsVisible(),
            canHide: column.getCanHide()
        }))

    const onChange = (ids: string[], visible: boolean) => {
        table.setColumnVisibility((old) => {
            const next = { ...old }
            for (const id of ids) {
                next[id] = visible
            }
            return next
        })
    }

    return <ColumnVisibilityButton columns={columns} onChange={onChange} />
}
